import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {sanitize} from '@github-ui/sanitize'
import {ChevronDownIcon, CopilotIcon} from '@primer/octicons-react'
import type {ReactNode} from 'react'
import {Suspense, useId, useMemo, useState} from 'react'
import {graphql, useLazyLoadQuery} from 'react-relay'

import type {IssuePlanBodyQuery as IssuePlanBodyQueryType} from './__generated__/IssuePlanBodyQuery.graphql'
import styles from './IssuePlanBody.module.css'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssuePlanBodyQuery = graphql`
  query IssuePlanBodyQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        planBody
        planBodyHTML
      }
    }
  }
`

type IssuePlanBodyProps = {
  issueId: string
  renderAction?: (planBody: string) => ReactNode
}

function IssuePlanBodyContent({issueId, renderAction}: IssuePlanBodyProps) {
  const data = useLazyLoadQuery<IssuePlanBodyQueryType>(IssuePlanBodyQuery, {id: issueId})
  const planBody = data.node?.planBody
  const planBodyHTML = data.node?.planBodyHTML

  if (!planBodyHTML || !planBody) {
    return null
  }

  const renderedAction = renderAction?.(planBody)

  return <IssuePlanBodyView planBodyHTML={planBodyHTML} renderedAction={renderedAction} />
}

function IssuePlanBodyView({planBodyHTML, renderedAction}: {planBodyHTML: string; renderedAction?: ReactNode}) {
  const sanitizedHTML = useMemo(() => sanitize(planBodyHTML), [planBodyHTML])
  const [isExpanded, setIsExpanded] = useState(true)
  const contentId = useId()

  return (
    <div className={styles.issuePlanBody} data-testid="issue-plan-body">
      <div className={styles.header}>
        <button
          type="button"
          className={styles.summaryButton}
          data-testid="issue-plan-summary"
          aria-expanded={isExpanded}
          aria-controls={contentId}
          onClick={() => setIsExpanded(previous => !previous)}
        >
          <span className={styles.summaryMain}>
            <span className={isExpanded ? styles.chevron : styles.chevronCollapsed}>
              <ChevronDownIcon size={16} />
            </span>
            <span className={styles.icon}>
              <CopilotIcon size={16} />
            </span>
            <span className={styles.title}>Implementation plan</span>
            <span className={styles.versionBadge}>v1</span>
          </span>
        </button>
        {renderedAction ? (
          <div className={styles.summaryAction} data-testid="plan-actions">
            {renderedAction}
          </div>
        ) : null}
      </div>

      {isExpanded ? (
        <div id={contentId} className={styles.content}>
          <MarkdownViewer verifiedHTML={sanitizedHTML} />
        </div>
      ) : null}
    </div>
  )
}

export function IssuePlanBody({issueId, renderAction}: IssuePlanBodyProps) {
  return (
    <ErrorBoundary boundaryName="IssuePlanBody" fallback={null} critical={false}>
      <Suspense fallback={null}>
        <IssuePlanBodyContent issueId={issueId} renderAction={renderAction} />
      </Suspense>
    </ErrorBoundary>
  )
}
